import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { toast } from 'sonner';
import { Search, Star, Award, Heart, MapPin, Briefcase, Filter, ChevronRight, Users, Trophy, MessageSquare, Zap, Flame } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function SalespeopleDirectory() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [salespeople, setSalespeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('');
  const [industry, setIndustry] = useState('');
  const [badge, setBadge] = useState('');
  const [minStars, setMinStars] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [showFilters, setShowFilters] = useState(false);

  const fetchSalespeople = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (region) params.append('region', region);
      if (industry) params.append('industry', industry);
      if (badge) params.append('badge', badge);
      if (minStars) params.append('minStars', minStars);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('limit', '30');

      const res = await fetch(`${API}/salespeople/search?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setSalespeople(data);
    } catch (error) {
      toast.error('Failed to load salespeople');
    } finally {
      setLoading(false);
    }
  }, [token, query, region, industry, badge, minStars, sortBy]);

  useEffect(() => { fetchSalespeople(); }, []);

  const handleSearch = () => fetchSalespeople();

  const toggleShortlist = async (e, sp) => {
    e.stopPropagation();
    try {
      const method = sp.shortlisted ? 'DELETE' : 'POST';
      const res = await fetch(`${API}/business/shortlist/${sp.id}`, {
        method,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      setSalespeople(prev => prev.map(s =>
        s.id === sp.id ? { ...s, shortlisted: !s.shortlisted } : s
      ));
      toast.success(sp.shortlisted ? 'Removed from shortlist' : 'Added to shortlist');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const badgeConfig = {
    GOLD: { label: 'Gold', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
    SILVER: { label: 'Silver', color: 'text-slate-300 bg-slate-300/10 border-slate-300/30' },
    BRONZE: { label: 'Bronze', color: 'text-amber-600 bg-amber-600/10 border-amber-600/30' }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-brand-bg" data-testid="salespeople-directory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-7 h-7 text-brand-gold" />
              <h1 className="font-display text-2xl sm:text-3xl text-brand-text tracking-wide">Salesperson Directory</h1>
            </div>
            <p className="text-brand-muted text-sm mt-1">Find top sales talent for your products</p>
          </div>

          {/* Search Bar */}
          <div className="rounded-xl border border-white/10 bg-[#111317] p-4 mb-6">
            <div className="flex gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
                <input
                  type="text"
                  placeholder="Search by name, skills, or bio..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-brand-text placeholder:text-brand-muted/60"
                  data-testid="directory-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm"
                  data-testid="directory-sort"
                >
                  <option value="rating">Best Rating</option>
                  <option value="reviews">Most Reviews</option>
                  <option value="deals">Most Deals</option>
                  <option value="recent">Recently Joined</option>
                </select>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-1 px-3 py-2.5 rounded-lg border text-sm ${showFilters ? 'bg-brand-gold/20 border-brand-gold/40 text-brand-gold' : 'bg-black/40 border-white/10 text-brand-text'}`}
                  data-testid="directory-filter-toggle"
                >
                  <Filter className="w-4 h-4" />
                  Filters
                </button>
                <button
                  onClick={handleSearch}
                  className="px-5 py-2.5 rounded-lg bg-brand-gold text-black font-medium hover:bg-brand-gold/90"
                  data-testid="directory-search-btn"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/5">
                <div>
                  <label className="block text-xs text-brand-muted mb-1">Region</label>
                  <input
                    type="text"
                    placeholder="e.g. Ontario"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm"
                    data-testid="directory-filter-region"
                  />
                </div>
                <div>
                  <label className="block text-xs text-brand-muted mb-1">Industry</label>
                  <input
                    type="text"
                    placeholder="e.g. SaaS"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm"
                    data-testid="directory-filter-industry"
                  />
                </div>
                <div>
                  <label className="block text-xs text-brand-muted mb-1">Badge</label>
                  <select
                    value={badge}
                    onChange={(e) => setBadge(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm"
                    data-testid="directory-filter-badge"
                  >
                    <option value="">Any</option>
                    <option value="GOLD">Gold</option>
                    <option value="SILVER">Silver</option>
                    <option value="BRONZE">Bronze</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-brand-muted mb-1">Min. Stars</label>
                  <select
                    value={minStars}
                    onChange={(e) => setMinStars(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-brand-text text-sm"
                    data-testid="directory-filter-minstars"
                  >
                    <option value="">Any</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                    <option value="4.5">4.5+</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <div className="text-brand-muted text-center py-20">Loading salespeople...</div>
          ) : salespeople.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-12 h-12 text-brand-muted/40 mx-auto mb-3" />
              <p className="text-brand-muted">No salespeople found matching your criteria</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {salespeople.map((sp) => (
                <SalespersonCard
                  key={sp.id}
                  sp={sp}
                  badgeConfig={badgeConfig}
                  isBusiness={user?.role === 'BUSINESS'}
                  onToggleShortlist={toggleShortlist}
                  onClick={() => navigate(`/salespeople/${sp.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

const SalespersonCard = ({ sp, badgeConfig, isBusiness, onToggleShortlist, onClick }) => {
  const b = sp.reputation?.badge ? badgeConfig[sp.reputation.badge] : null;
  const labelConfig = {
    'Top Rated': { icon: Star, color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
    'Most Deals Won': { icon: Trophy, color: 'text-green-400 bg-green-400/10 border-green-400/30' },
    'Most Reviewed': { icon: MessageSquare, color: 'text-blue-400 bg-blue-400/10 border-blue-400/30' }
  };

  return (
    <div
      onClick={onClick}
      data-testid="salesperson-card"
      className="group rounded-xl border border-white/10 bg-[#111317] p-5 cursor-pointer hover:border-brand-gold/30 transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {sp.avatar ? (
            <img src={sp.avatar} alt={sp.name} className="w-12 h-12 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-semibold text-lg">
              {sp.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-brand-text font-semibold group-hover:text-brand-gold transition-colors">{sp.name}</span>
              {b && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${b.color}`}>
                  <Award className="w-2.5 h-2.5 inline mr-0.5" />{b.label}
                </span>
              )}
            </div>
            {/* Top Performer Labels */}
            {sp.labels?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {sp.labels.map((label, i) => {
                  const lc = labelConfig[label];
                  if (!lc) return null;
                  const LIcon = lc.icon;
                  return (
                    <span key={i} data-testid="card-label" className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${lc.color}`}>
                      <LIcon className="w-2.5 h-2.5 inline mr-0.5" />{label}
                    </span>
                  );
                })}
              </div>
            )}
            {sp.regions?.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-brand-muted mt-0.5">
                <MapPin className="w-3 h-3" />{sp.regions.join(', ')}
              </div>
            )}
          </div>
        </div>

        {isBusiness && (
          <button
            onClick={(e) => onToggleShortlist(e, sp)}
            data-testid="shortlist-btn"
            className="p-1.5 rounded-lg hover:bg-white/5"
          >
            <Heart className={`w-5 h-5 ${sp.shortlisted ? 'fill-red-500 text-red-500' : 'text-brand-muted hover:text-red-400'}`} />
          </button>
        )}
      </div>

      {/* Stats Row */}
      <div className="flex items-center gap-4 mb-3 text-sm">
        <div className="flex items-center gap-1">
          <Star className="w-4 h-4 text-brand-gold fill-brand-gold" />
          <span className="text-brand-text font-medium">{sp.reputation?.avgStars?.toFixed(1) || '0.0'}</span>
          <span className="text-brand-muted">({sp.reputation?.reviewsCount || 0})</span>
        </div>
        {sp.completedDeals > 0 && (
          <div className="flex items-center gap-1 text-brand-muted">
            <Briefcase className="w-3.5 h-3.5" />
            <span>{sp.completedDeals} deal{sp.completedDeals !== 1 ? 's' : ''}</span>
          </div>
        )}
        {sp.xp > 0 && (
          <div className="flex items-center gap-1 text-purple-400">
            <Zap className="w-3.5 h-3.5" />
            <span>{sp.xp} XP</span>
          </div>
        )}
        {sp.streak > 0 && (
          <div className="flex items-center gap-1 text-orange-400">
            <Flame className="w-3.5 h-3.5" />
            <span>{sp.streak} mo</span>
          </div>
        )}
      </div>

      {/* Top Achievements */}
      {sp.topAchievements?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="card-achievements">
          {sp.topAchievements.map((ach) => {
            const achIcon = { briefcase: Briefcase, star: Star, award: Award, flame: Flame, trophy: Trophy }[ach.icon] || Award;
            const AchIcon = achIcon;
            const achColor = { deals: 'text-green-400 bg-green-400/8 border-green-500/20', reputation: 'text-brand-gold bg-brand-gold/8 border-brand-gold/20', consistency: 'text-orange-400 bg-orange-400/8 border-orange-400/20' }[ach.category] || 'text-zinc-400 bg-zinc-400/8 border-zinc-400/20';
            return (
              <span key={ach.key} data-testid={`card-ach-${ach.key}`} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border font-medium ${achColor}`}>
                <AchIcon className="w-2.5 h-2.5" />{ach.name}
              </span>
            );
          })}
        </div>
      )}

      {/* Skills */}
      {sp.skills?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {sp.skills.slice(0, 4).map((skill, i) => (
            <span key={i} className="text-xs px-2 py-0.5 rounded bg-white/5 text-brand-muted border border-white/5">{skill}</span>
          ))}
          {sp.skills.length > 4 && <span className="text-xs text-brand-muted">+{sp.skills.length - 4}</span>}
        </div>
      )}

      {/* Industries */}
      {sp.industries?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {sp.industries.slice(0, 3).map((ind, i) => (
            <span key={i} className="text-xs px-2 py-0.5 rounded bg-brand-gold/5 text-brand-gold/70 border border-brand-gold/10">{ind}</span>
          ))}
        </div>
      )}

      {/* View Profile Arrow */}
      <div className="flex justify-end mt-3">
        <ChevronRight className="w-4 h-4 text-brand-muted group-hover:text-brand-gold transition-colors" />
      </div>
    </div>
  );
};

export default SalespeopleDirectory;
